import { useTranslation } from "react-i18next";
import { Grid, GridColumn, GridRow } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components";
import { STATUS_IDENTIFIERS } from "models";
import { ActivityType } from "modules/AppointmentDetails/components/ActivityLog/components";
import { ACTIVITY_LOG_TYPE, useActivityLogs } from "modules/AppointmentDetails/components/ActivityLog/utils";
import "modules/AppointmentDetails/components/Interventions/components/PinOverview/components/WarrantyLogSection/WarrantyLogSection.scss";
import { formatDate } from "utils/dateHelperFunctions";

import type { Appointment } from "models";
import type { ActivityLogs } from "modules/AppointmentDetails/components/ActivityLog/utils";

type Props = {
  showCustomerInformation: boolean;
  appointment?: Appointment;
};

const CUSTOMER_ACTIVITY_TYPES = [
  ACTIVITY_LOG_TYPE.Customer,
  ACTIVITY_LOG_TYPE.Desk,
  ACTIVITY_LOG_TYPE.Kiosk,
  ACTIVITY_LOG_TYPE.Keylocker,
  ACTIVITY_LOG_TYPE.AcsesReceiver
];

const getVisibleName = (log: ActivityLogs, showCustomerInformation: boolean) => {
  if (showCustomerInformation) return log.name;

  const isCustomerContact = CUSTOMER_ACTIVITY_TYPES.includes(log.activityType) || log.statusIdentifier === STATUS_IDENTIFIERS.CustomerAnsweredStatus;
  return isCustomerContact ? "" : log.name;
};

export const WarrantyLogSection = ({ showCustomerInformation, appointment }: Props) => {
  const { t } = useTranslation();

  const activityLogs = useActivityLogs(appointment);

  return (
    <div className="WarrantyLogSectionContent">
      {activityLogs.length > 0 && (
        <>
          <h2 className="title">{t("v8_activity_log", "Activity log")}</h2>
          <Grid padded="horizontally" className="grid-header-container">
            <GridRow verticalAlign="middle">
              <GridColumn width={5}>
                <span className="grid-title-text">{t("v8_activity", "Activity")}</span>
              </GridColumn>
              <GridColumn width={4}>
                <span className="grid-title-text">{t("v8_user_customer", "User / Customer")}</span>
              </GridColumn>
              <GridColumn width={4}>
                <span className="grid-title-text">{t("v8_time", "Time")}</span>
              </GridColumn>
              <GridColumn width={3}>
                <span className="grid-title-text">{t("v8_note", "Note")}</span>
              </GridColumn>
            </GridRow>
          </Grid>
          <Grid padded="horizontally" className="results-grid">
            {activityLogs.map(log => (
              <GridRow verticalAlign="middle" key={`${log.time}-${log.activityType}-${log.name}-${log.note}`}>
                <GridColumn width={5}>
                  <ActivityType
                    activityType={log.activityType}
                    statusIdentifier={log.statusIdentifier}
                    isDeskCheckOut={log.isDeskCheckOut}
                    keylockerPinRequestType={log.keylockerPinRequestType}
                    activityTitle={log.activityTitle}
                    event_type={log.eventType}
                    appointmentLogType={log.appointmentLogType}
                  />
                </GridColumn>
                <GridColumn width={4}>
                  <span className="grid-field-text">{getVisibleName(log, showCustomerInformation)}</span>
                </GridColumn>
                <GridColumn width={4}>
                  <span className="grid-time-text">{formatDate(log.time, log.isDateOnly ? DATE_FORMATS_FNS.dateMonthYear : DATE_FORMATS_FNS.dateMonthYearTime)}</span>
                </GridColumn>
                <GridColumn width={3}>
                  <span className="grid-field-text">{log.note}</span>
                </GridColumn>
              </GridRow>
            ))}
          </Grid>
        </>
      )}
    </div>
  );
};
